import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Heart,
  Globe,
  Briefcase,
  CheckCircle,
  ArrowRight,
  MapPin,
  Clock
} from 'lucide-react';

const Careers = () => {
  const openRoles = [
    {
      title: "Telehealth Coordinator",
      department: "Telehealth Services",
      location: "Bamdzeng – Nso, Cameroon & The Gambia",
      type: "Full-time",
      description: "Coordinate telehealth consultations with Western board-certified physicians and manage patient referrals."
    },
    {
      title: "School Enrollment Manager",
      department: "Enrollment",
      location: "Bamdzeng – Nso, Cameroon & The Gambia",
      type: "Full-time",
      description: "Manage school enrollments, enrollment processes, and health education programs."
    },
    {
      title: "Claims Processing Specialist",
      department: "Operations",
      location: "Bamdzeng – Nso, Cameroon & The Gambia",
      type: "Full-time",
      description: "Process claims from provider clinics and ensure fast reimbursement for covered services."
    },
    {
      title: "Community Health Educator",
      department: "Prevention Programs",
      location: "Field-based",
      type: "Full-time",
      description: "Deliver health education programs on hygiene, nutrition, and preventive care in schools and communities."
    }
  ];

  const volunteerOpportunities = [
    {
      title: "School Health Days",
      description: "Help organize and run health education sessions at provider schools",
      timeCommitment: "4-8 hours per event",
      location: "Various schools across Cameroon & The Gambia"
    },
    {
      title: "Translation Support",
      description: "Translate materials between English, French, and local languages",
      timeCommitment: "2-4 hours per week",
      location: "Remote"
    },
    {
      title: "Community Outreach",
      description: "Support community engagement and awareness campaigns",
      timeCommitment: "6-10 hours per week",
      location: "Various communities"
    },
    {
      title: "Administrative Support",
      description: "Assist with data entry, filing, and administrative tasks",
      timeCommitment: "4-6 hours per week",
      location: "Yaoundé office"
    }
  ];

  const benefits = [
    "Competitive salary and benefits package",
    "Health insurance coverage for employees and families",
    "Professional development and training opportunities",
    "Flexible work arrangements where possible",
    "Opportunity to make a real impact on child health",
    "Collaborative and supportive work environment"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white" style={{ paddingTop: 'calc(5rem + 300px)', paddingBottom: 'calc(5rem + 300px)' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Careers & Volunteers</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Join us in creating a healthier future for children
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Why Work With AMHAS?</h2>
            <p className="section-subtitle">
              Be part of a mission-driven organization making real impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Mission-Driven",
                description: "Work for a cause that directly improves children's health and wellbeing"
              },
              {
                icon: Globe,
                title: "National Impact",
                description: "Contribute to healthcare access across Cameroon and beyond"
              },
              {
                icon: Users,
                title: "Collaborative Team",
                description: "Join a diverse, passionate team working toward common goals"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8 text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={32} className="text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Open Positions</h2>
            <p className="section-subtitle">
              Join our team and help us expand our impact
            </p>
          </motion.div>

          <div className="space-y-6">
            {openRoles.map((role, index) => (
              <motion.div
                key={role.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{role.title}</h3>
                    <p className="text-gray-600 mb-4">{role.description}</p>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <Briefcase size={16} />
                        <span>{role.department}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MapPin size={16} />
                        <span>{role.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock size={16} />
                        <span>{role.type}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 md:mt-0 md:ml-6">
                    <button className="btn-primary">
                      Apply Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Employee Benefits</h2>
            <p className="section-subtitle">
              We value our team and offer comprehensive benefits
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-3"
              >
                <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Volunteer Opportunities</h2>
            <p className="section-subtitle">
              Make a difference in your community through volunteering
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {volunteerOpportunities.map((opportunity, index) => (
              <motion.div
                key={opportunity.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">{opportunity.title}</h3>
                <p className="text-gray-600 mb-4">{opportunity.description}</p>

                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <Clock size={16} />
                    <span>{opportunity.timeCommitment}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin size={16} />
                    <span>{opportunity.location}</span>
                  </div>
                </div>

                <button className="mt-4 text-primary-600 hover:text-primary-700 font-semibold flex items-center space-x-2">
                  <span>Learn More</span>
                  <ArrowRight size={16} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">How to Apply</h2>
            <p className="section-subtitle">
              Simple steps to join our team
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Browse Positions", description: "Review open roles and volunteer opportunities" },
              { step: "2", title: "Submit Application", description: "Send your CV and cover letter to careers@amhas.org" },
              { step: "3", title: "Interview Process", description: "Participate in interviews and assessments" },
              { step: "4", title: "Join Our Team", description: "Welcome to AMHAS! Start making an impact" }
            ].map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Join Us?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Whether you're looking for a career opportunity or want to volunteer,
              we'd love to hear from you.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="mailto:careers@amhas.org" className="btn-secondary">
                Apply for Position
              </a>
              <a href="mailto:volunteers@amhas.org" className="btn-secondary">
                Volunteer Interest
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
